import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import { UseFormReturn } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import FormButtons from 'components/form/FormButtons';
import Button from 'components/Button';
import { LeavesAvailabilityDropdowns, LeavesAvailabilityReportFormData } from '../types';

interface IProps {
  methods: UseFormReturn<LeavesAvailabilityReportFormData>;
  data: LeavesAvailabilityDropdowns;
  onSubmit: (data: LeavesAvailabilityReportFormData) => void;
  onDownload: (data: LeavesAvailabilityReportFormData) => void;
  isDownloading: boolean;
}

const ReportForm: FC<IProps> = ({ methods, data, onSubmit, onDownload, isDownloading }) => {
  const { t } = useTranslation();
  const { formState } = methods;
  const { isSubmitting } = formState;
  const [employees, setEmployees] = useState(data?.user || []);

  useEffect(() => {
    methods.setValue(
      'status',
      data?.status.filter(option => option.selected)
    );
  }, []);

  const groups = methods.watch('group_id');

  useEffect(() => {
    const groupIds = groups?.map(gr => gr.value);

    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds) {
      const filteredEmployees = data?.user.filter(empl => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag, currGr) => flag || groupIds.includes(currGr), false);
        return isInSelectedGroups;
      });

      setEmployees(filteredEmployees);
      methods.setValue('user_id', filteredEmployees);
    } else {
      setEmployees(data?.user);
      methods.setValue('user_id', null);
    }
  }, [groups]);

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.viewLeavesAvailabilityReport')}
      hideAllBtns
      onSubmit={onSubmit}
    >
      <Row>
        <Col md={4}>
          <FormGroup
            controlId="formGroupGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col md={4}>
          <FormGroup
            controlId="formGroupEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            selectOptions={employees}
            limitOptions
          />
        </Col>
        <Col md={4}>
          <FormGroup
            controlId="formGroupLeaveStatus"
            label={t('reports.leaveStatus')}
            type="select"
            as="select"
            name="status"
            isMulti
            isClearable
            isRequired
            isSearchable
            allowSelectAll
            selectOptions={data?.status}
          />
        </Col>
      </Row>
      <div className="d-flex flex-column flex-sm-row justify-content-center mb-5">
        <FormButtons
          btnLabel={t('reports.viewLeavesAvailabilityReport')}
          isSubmitting={isSubmitting}
          hideCancelBtn
        />
        <div className="pt-2 pl-sm-3 text-center text-sm-auto">
          <Button
            btnType="secondary"
            type="button"
            disabled={isDownloading}
            loading={isDownloading}
            onClick={() => onDownload(methods.getValues())}
          >
            <FaDownload
              size={14}
              className="mr-2"
            />
            {t('reports.downloadLeavesReport')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default ReportForm;
